import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { 
  FaImages, 
  FaArrowLeft, 
  FaTimes, 
  FaExpand, 
  FaFolderOpen, 
  FaRegImage 
} from "react-icons/fa";
import "./Gallery.css";
import axiosInstance from "../../api/axiosInstance";

const API_BASE_URL = "http://localhost:5000"; // Adjust as per your backend environment

function Gallery() {
  const [albums, setAlbums] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAndGroupGalleryData = async () => {
      try {
        const response = await axiosInstance.get(`/api/gallery/public`);
        const data = response.data;

        if (Array.isArray(data)) {
          const groupedByFolder = data.reduce((acc, item) => {
            const folder = item.folderName || "General Showcase";
            if (!acc[folder]) {
              acc[folder] = [];
            }
            acc[folder].push({
              url: `${API_BASE_URL}${item.image}`,
              desc: item.description || "",
            });
            return acc;
          }, {});

          const albumArray = Object.keys(groupedByFolder).map((title, index) => ({
            id: index + 1,
            title: title,
            images: groupedByFolder[title] || [],
          }));

          setAlbums(albumArray);
        }
      } catch (error) {
        console.error("Failed to fetch gallery data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAndGroupGalleryData();
  }, []);

  useEffect(() => {
    document.body.style.overflow = selectedImage ? "hidden" : "auto";
  }, [selectedImage]);

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToAlbums = () => setActiveCategory(null);
  const handleImageClick = (img) => setSelectedImage(img);
  const handleCloseLightbox = () => setSelectedImage(null);

  const getThumbnail = (album, index) => {
    return album.images && album.images[index] ? album.images[index].url : null;
  };

  return (
    <>
      <Helmet>
        <title>Our Visual Showcase | Nutrivio Medical Services</title>
        <meta
          name="description"
          content="Explore visual showcases of medical college campuses, teaching hospital setups, advanced laboratories, and infrastructure supported by Nutrivio."
        />
      </Helmet>

      <div className="gallery-page-wrapper">
        <div className="gallery-ambient-pattern"></div>

        {/* --- HEADER WITH LEFT & RIGHT DASHES --- */}
        <div className="gallery-header-container">
          <div className="gallery-header-eyebrow">
            <span className="gallery-eyebrow-dash"></span>
            <span>Institutional Visuals</span>
            <span className="gallery-eyebrow-dash"></span>
          </div>
          <h1 className="gallery-main-heading">Our Visual Showcase</h1>
        </div>

        {/* --- MAIN CONTENT AREA --- */}
        <div className="gallery-main-content">
          {!activeCategory ? (
            /* ================= FOLDERS / ALBUMS VIEW ================= */
            loading ? (
              <div className="gallery-loading-state">
                <div className="gallery-spinner"></div>
                <p>Loading Visual Collections...</p>
              </div>
            ) : albums.length === 0 ? (
              <div className="gallery-empty-state">
                <div className="gallery-empty-icon-frame">
                  <FaRegImage />
                </div>
                <h4>No Collections Available</h4>
                <p>Visual records are currently being updated. Please check back shortly.</p>
              </div>
            ) : (
              <div className="gallery-albums-grid">
                {albums.map((album) => {
                  const hasImages = album.images && album.images.length > 0;
                  const mainThumb = getThumbnail(album, 0);
                  const subThumb1 = getThumbnail(album, 1);
                  const subThumb2 = getThumbnail(album, 2);

                  return (
                    <div
                      key={album.id}
                      className="gallery-folder-card"
                      onClick={() => handleCategoryClick(album)}
                    >
                      {/* Top Meta Bar */}
                      <div className="gallery-folder-top-bar">
                        <div className="gallery-folder-tag">
                          <FaFolderOpen className="gallery-folder-icon" />
                          <span>Collection</span>
                        </div>
                        <div className="gallery-folder-counter">
                          <FaImages /> {album.images.length} {album.images.length === 1 ? "Photo" : "Photos"}
                        </div>
                      </div>

                      {/* Asymmetric Preview Grid */}
                      <div className="gallery-folder-preview-wrap">
                        {hasImages ? (
                          <div className="gallery-folder-preview-grid">
                            <div className="gallery-preview-main">
                              {mainThumb ? (
                                <img src={mainThumb} alt={album.title} loading="lazy" />
                              ) : (
                                <div className="gallery-fallback-cell">
                                  <FaRegImage />
                                </div>
                              )}
                            </div>
                            <div className="gallery-preview-sidebar">
                              <div className="gallery-preview-sub">
                                {subThumb1 ? (
                                  <img src={subThumb1} alt="Preview 1" loading="lazy" />
                                ) : (
                                  <div className="gallery-fallback-cell small">
                                    <FaRegImage />
                                  </div>
                                )}
                              </div>
                              <div className="gallery-preview-sub">
                                {subThumb2 ? (
                                  <img src={subThumb2} alt="Preview 2" loading="lazy" />
                                ) : (
                                  <div className="gallery-fallback-cell small">
                                    <FaRegImage />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* Fallback when folder is completely empty */
                          <div className="gallery-empty-preview-block">
                            <div className="gallery-empty-art">
                              <FaRegImage className="gallery-empty-art-icon" />
                              <span>No Visuals Uploaded</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Bottom Info Bar */}
                      <div className="gallery-folder-footer">
                        <h3 className="gallery-folder-title">{album.title}</h3>
                        <span className="gallery-folder-action-text">View Album &rarr;</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            /* ================= INSIDE FOLDER: IMAGES VIEW ================= */
            <div className="gallery-images-view">
              <div className="gallery-view-header">
                <button
                  type="button"
                  className="gallery-back-btn"
                  onClick={handleBackToAlbums}
                >
                  <FaArrowLeft /> Back to Collections
                </button>
                <div className="gallery-active-title-wrap">
                  <span className="gallery-active-eyebrow">Viewing Collection</span>
                  <h2 className="gallery-active-folder-title">{activeCategory.title}</h2>
                </div>
              </div>

              {activeCategory.images.length === 0 ? (
                <div className="gallery-empty-state inside">
                  <div className="gallery-empty-icon-frame">
                    <FaRegImage />
                  </div>
                  <h4>No Images in this Collection</h4>
                  <p>There are no photos uploaded inside this folder yet.</p>
                </div>
              ) : (
                <div className="gallery-images-grid">
                  {activeCategory.images.map((img, index) => (
                    <div
                      key={index}
                      className="gallery-image-card"
                      onClick={() => handleImageClick(img)}
                    >
                      <img
                        src={img.url}
                        alt={img.desc || activeCategory.title}
                        loading="lazy"
                      />
                      <div className="gallery-image-overlay">
                        <div className="gallery-expand-icon">
                          <FaExpand />
                        </div>
                        {img.desc && img.desc.trim() !== "" && (
                          <span className="gallery-image-caption-tag">{img.desc}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* --- FULLSCREEN LIGHTBOX MODAL --- */}
        {selectedImage && (
          <div className="gallery-lightbox-overlay" onClick={handleCloseLightbox}>
            <div
              className="gallery-lightbox-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="gallery-lightbox-image-container">
                <img src={selectedImage.url} alt="Showcase Full View" />
                <button
                  type="button"
                  className="gallery-lightbox-close-btn"
                  onClick={handleCloseLightbox}
                  aria-label="Close Lightbox"
                >
                  <FaTimes />
                </button>
              </div>
              {selectedImage.desc && selectedImage.desc.trim() !== "" && (
                <div className="gallery-lightbox-caption">
                  <p>{selectedImage.desc}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Gallery;